/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/itp-export/useQaItpFrom.ts
 * FILE_ID: JOBHUB-QA-ITP-FROM-001
 * PURPOSE: QA-only From mailbox. Does not change the Email hub default.
 */
import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@quotech/auth'
// Reused from Emails Hub: packages/email-hub/src/feature/compose/fromDefaultSync.ts (GET/PUT /api/settings/email/ partial merge)
import { emailApiFetch, setEmailTenantId } from '@quotech/email-hub/feature/lib/api'

export interface QaSendableAccount {
  id: number
  email_address?: string
  display_name?: string
  owner_user_id?: number | string | null
  account_id?: number | null
  provider?: string
  can_send?: boolean
  send_blocked_reason?: string
}

export function chooseQaFrom({
  sendable,
  saved,
  userEmail,
  userId,
}: {
  sendable: QaSendableAccount[]
  saved: string
  userEmail: string
  userId: unknown
}): string {
  if (saved.startsWith('account:')) {
    const id = Number(saved.slice('account:'.length))
    if (sendable.some((account) => account.id === id)) return saved
  }
  const own = sendable.find((account) =>
    (account.email_address || '').toLowerCase() === userEmail.toLowerCase() && userEmail !== '')
  if (own) return `account:${own.id}`
  const owned = sendable.find((account) =>
    userId != null && userId !== '' && String(account.owner_user_id) === String(userId))
  if (owned) return `account:${owned.id}`
  return 'quotech'
}

export function qaFromPayload(
  from: string,
  userEmail = '',
): { from_account_id: number } | { send_method: 'smtp'; from_email?: string } {
  if (from === 'login') return { send_method: 'smtp', from_email: userEmail }
  if (from.startsWith('account:')) return { from_account_id: Number(from.slice('account:'.length)) }
  return { send_method: 'smtp' }
}

export function qaUserName(user: {
  name?: string
  full_name?: string
  first_name?: string
  last_name?: string
  email?: string
} | null | undefined): string {
  // Reused from: packages/auth/src/useAuth.ts (userName)
  const named = (user?.name || user?.full_name || '').trim()
  if (named) return named
  const parts = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim()
  return parts || (user?.email || '')
}

export function useQaItpFrom(
  orgId?: number,
  inspectionId?: string,
  listItpFromMailboxes?: (inspectionId: string) => Promise<{ accounts: QaSendableAccount[] }>,
) {
  // Reused from: packages/tenant-shared/src/email/api.ts (setEmailTenantId)
  if (typeof orgId === 'number') setEmailTenantId(orgId)
  const { user } = useAuth()
  const [accounts, setAccounts] = useState<QaSendableAccount[] | null>(null)
  const sendable = accounts ?? []
  const userEmail = user?.email || ''
  const userId = user?.id
  const [saved, setSaved] = useState('')
  const [savedMessage, setSavedMessage] = useState('')
  const [from, setFromState] = useState('quotech')
  const [saving, setSaving] = useState(false)
  const [savingMessage, setSavingMessage] = useState(false)
  const [prefsLoaded, setPrefsLoaded] = useState(false)
  const picked = useRef(false)
  const loadMailboxes = useRef(listItpFromMailboxes)
  loadMailboxes.current = listItpFromMailboxes

  useEffect(() => {
    const load = loadMailboxes.current
    if (!inspectionId || typeof load !== 'function') {
      setAccounts((current) => (current && current.length === 0 ? current : []))
      return
    }
    let cancel = false
    void load(inspectionId).then((data) => {
      if (!cancel) setAccounts(Array.isArray(data?.accounts) ? data.accounts : [])
    }).catch(() => {
      if (!cancel) setAccounts([])
    })
    return () => { cancel = true }
  }, [inspectionId])

  useEffect(() => {
    let cancel = false
    void emailApiFetch('/api/settings/email/').then(async (res) => {
      if (!res.ok || cancel) return
      const data = await res.json().catch(() => null)
      const prefs = data?.preferences ?? data
      const value = typeof prefs?.qa_default_from === 'string' ? prefs.qa_default_from : ''
      const message = typeof prefs?.qa_default_message === 'string' ? prefs.qa_default_message : ''
      if (!cancel) {
        setSaved(value)
        setSavedMessage(message)
      }
    }).catch(() => undefined).finally(() => {
      if (!cancel) setPrefsLoaded(true)
    })
    return () => { cancel = true }
  }, [])

  useEffect(() => {
    if (picked.current || accounts == null || !prefsLoaded) return
    setFromState(chooseQaFrom({ sendable: accounts, saved, userEmail, userId }))
  }, [accounts, prefsLoaded, saved, userEmail, userId])

  const setFrom = (value: string) => {
    picked.current = true
    setFromState(value)
  }

  const saveDefault = async () => {
    setSaving(true)
    try {
      const res = await emailApiFetch('/api/settings/email/', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferences: { qa_default_from: from } }),
      })
      if (res.ok) setSaved(from)
    } finally {
      setSaving(false)
    }
  }

  const saveDefaultMessage = async (text: string) => {
    setSavingMessage(true)
    try {
      const res = await emailApiFetch('/api/settings/email/', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferences: { qa_default_message: text } }),
      })
      if (res.ok) setSavedMessage(text)
    } finally {
      setSavingMessage(false)
    }
  }

  return {
    from,
    setFrom,
    sendable,
    userEmail,
    userName: qaUserName(user),
    unconnectedLogin: userEmail !== '' && !sendable.some((account) =>
      (account.email_address || '').toLowerCase() === userEmail.toLowerCase()),
    isDefault: saved !== '' && from === saved,
    prefsLoaded,
    savedMessage,
    saveDefault,
    saveDefaultMessage,
    saving,
    savingMessage,
    payload: qaFromPayload(from, userEmail),
  }
}
